<template>
  <div class="ManagementDashboard">
    <p v-if="!isElaadForecast">
      {{ $t("components.navbar.managementDashboard.noData") }}
    </p>
    <div v-else-if="!isLoading && isElaadForecast && numPrognose !== 0">
      <div class="data-container">
        <div class="column">
          <div class="header" />
          <div>
            {{ $t("components.navbar.managementDashboard.forecast") }}
            {{ year }}
          </div>
          <div>{{ $t("groupLabels.realized") }}</div>
          <div>{{ $t("groupLabels.in-progress") }}</div>
          <div>{{ $t("groupLabels.suggestion") }}</div>
          <div>{{ $t("groupLabels.definitive") }}</div>
          <div class="border-top">
            <i>{{ $t("components.navbar.managementDashboard.toSuggest") }}</i>
          </div>
        </div>
        <div class="column">
          <div class="header">
            <b>{{ $t("components.navbar.managementDashboard.numSemiPublic") }}</b>
          </div>
          <div class="text-right">
            {{ numPrognose }}
          </div>
            <div class="text-right">
            <b-spinner small variant="light" v-if="chargingPoints.length === 0" />
            <span v-else>{{ numRealized }}</span>
            </div>
            <div class="text-right">
            <b-spinner small variant="light" v-if="chargingPoints.length === 0" />
            <span v-else>{{ numInProgress }}</span>
            </div>
            <div class="text-right">
            <b-spinner small variant="light" v-if="chargingPoints.length === 0" />
            <span v-else>{{ numSuggested }}</span>
            </div>
            <div class="text-right">
            <b-spinner small variant="light" v-if="chargingPoints.length === 0" />
            <span v-else>{{ numValidated }}</span>
            </div>
            <div class="border-top text-right">
            <b-spinner small variant="light" v-if="chargingPoints.length === 0" />
            <i v-else>{{ numToSuggest }}</i>
            </div>
        </div>
      </div>
    </div>
    <div v-else>
      <span>{{ $t("loading") }}</span>
      <b-spinner
        small
        variant="primary"
        class="flex justify-center"
      />
    </div>
  </div>
</template>

<script>
import { EventBus } from '@/services/eventbus'
import { mapGetters } from 'vuex'
import chargingpointsLoadMixin from '@/mixins/chargingpoint/chargingpointsLoadMixin'

export default {
  name: 'ManagementDashboard',
  mixins: [chargingpointsLoadMixin],
  data() {
    return {
      isLoading: false,
      chargingPoints: [],
      numPrognose: 0,
    }
  },
  computed: {
    ...mapGetters('scenarios', ['year', 'forecast']),
    ...mapGetters('access', ['getActiveMunicipality', 'getActiveMunicipalityList', 'hasProxy']),
    numRealized() {
      return this.fetchChargingPointsByStatus('realized')
    },
    numInProgress() {
      return this.fetchChargingPointsByStatus('in-progress')
    },
    numSuggested() {
      return this.fetchChargingPointsByStatus('suggestion')
    },
    numValidated() {
      return this.fetchChargingPointsByStatus('definitive')
    },
    numToSuggest() {
      return (
        this.numPrognose -
        this.numRealized -
        this.numValidated -
        this.numInProgress -
        this.numSuggested
      )
    },
    isElaadForecast() {
      return [
        'elaad_buurt_low',
        'elaad_buurt_mid',
        'elaad_buurt_high',
        'elaad_combinatie',
      ].includes(this.forecast)
    },
    source() {
      return this.$store.map.getSource(this.forecast)
    },
  },
  watch: {
    getActiveMunicipality() {
      if (this.isElaadForecast) {
        this.activate()
        this.fetchAllChargingPoints()
      }
    },
    forecast() {
      if (this.isElaadForecast) {
        this.activate()
        this.fetchAllChargingPoints()
      }
    },
    isElaadForecast(newValue) {
      if (newValue && this.getActiveMunicipality) {
        this.activate()
        this.fetchAllChargingPoints()
      }
    },
  },
  async created() {
    this.activate()

    if (!this.isElaadForecast) return

    this.getForecastValue()
    this.fetchAllChargingPoints()
  },
  beforeDestroy() {
    this.disable()
  },
  methods: {
    activate() {
      this.disable()
      EventBus.$on('overlay-click', this.maybeClose)
    },
    disable() {
      EventBus.$off('overlay-click', this.maybeClose)
    },
    async getElaadData({ layer }) {
      try {
        const token = await this.$auth.getTokenSilently()

        const apiCall = await fetch('/api/get-maps-data', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ layer }),
        })

        const response = await apiCall.json()

        return response
      } catch (error) {
        console.log(error)
      }
    },
    async getForecastValue() {
      this.isLoading = true
      const { data } = await this.getElaadData({ layer: 'elaadBuurt2024' })

      if (this.hasProxy) {
        const propertyNameList = this.getActiveMunicipalityList.map(munCode =>
          this.generateFieldName(munCode, this.year)
        )
        const matchingFeatures = data.features.filter((obj) =>
          propertyNameList.includes(obj.properties.key),
        )
        const total = matchingFeatures.reduce(
          (sum, obj) => sum + (Number(obj.properties.value) || 0),
          0,
        )
        this.numPrognose = Math.round(total)
        this.isLoading = false
        return
      }

      const propertyName = this.generateFieldName(
        this.getActiveMunicipality,
        this.year,
      )
      const numStations = data.features.find(
        (obj) => obj.properties.key === propertyName,
      )

      if (numStations) {
        this.numPrognose = Math.round(numStations.properties.value)
      } else {
        this.numPrognose = 0
        this.isLoading = false
      }
      this.isLoading = false
    },
    async fetchAllChargingPoints() {
      try {
        this.isLoading = true

        this.chargingPoints =
          await this.$_chargingpointsLoadMixin_loadChargingPoints({
            code: this.getActiveMunicipality,
          })

        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        console.error('Error fetching charging points:', error)
      }
    },
    fetchChargingPointsByStatus(status) {
      return this.chargingPoints
        .filter((chargingpoint) => {
          return (
            chargingpoint.properties?.status?.includes(status) &&
            !chargingpoint.properties?.status?.includes('private')
          )
        })
        .reduce((sum, chargingpoint) => {
          // Faulty imports can write nroutlets as text ("2", "Onbekend"), which would
          // concatenate instead of add. Only whole numbers count, anything else
          // falls back to the default of 2 outlets per location.
          const nroutlets = chargingpoint.properties?.nroutlets
          return sum + (Number.isInteger(nroutlets) ? nroutlets : 2)
        }, 0)
    },
    generateFieldName(munCode, year) {
      if (this.forecast === 'elaad_combinatie') {
      // pattern used in the combinaite layer
      return `tot_laadpunten_${munCode}_${year}_totaal_laadpunten_C`
      }

      const suffix = this.forecast.endsWith('low')
      ? 'L'
      : this.forecast.endsWith('mid')
      ? 'M'
      : this.forecast.endsWith('high')
      ? 'H'
      : ''

      return `tot_semi_publiek_${munCode}_${year}_semi_publiekelaadpunten_${suffix}`
    },
    maybeClose() {
      EventBus.$emit('overlay-hide')
    },
  },
}
</script>

<style lang="scss">
.ManagementDashboard {
  position: relative;
  background: var(--primary);
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999999;
  opacity: 0.999;
  padding: 1rem;
  min-width: 20rem;
  min-height: 5rem;

  p {
    align-self: center;
    margin: 1rem;
    max-width: 100%;
  }

  .data-container {
    display: flex;
  }

  .column {
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
  }

  .header {
    font-weight: bold;
    padding-bottom: 0.5rem;
    height: 24px;
  }
}
</style>
